import React from "react";
import ReactDOM from "react-dom";
import ImageLightbox from "../components/organisms/ImageLightbox";
import { normalizeMediaUrl, parseImagesFromHtml } from "../utils/imageUtils";

export default function useImageLightbox(options = {}) {
  const { variant = 'modal', tone = 'dark', showThumbnails = false } = options || {};
  const [state, setState] = React.useState({ open: false, items: [], index: 0 });
  const portalRoot = React.useMemo(() => {
    if (typeof document === "undefined") return null;
    return document.getElementById("portal") || document.body;
  }, []);

  const openWithItems = React.useCallback((items, startIndex = 0) => {
    const list = Array.isArray(items) ? items.filter((i) => i && i.src) : [];
    if (!list.length) return;
    const idx = Number.isFinite(startIndex) ? Math.max(0, Math.min(startIndex, list.length - 1)) : 0;
    setState({ open: true, items: list, index: idx });
  }, []);

  const close = React.useCallback(() => setState({ open: false, items: [], index: 0 }), []);

  const getRichContentClickHandler = React.useCallback((html) => (e) => {
    try {
      const target = e?.target;
      if (!target || target.tagName !== 'IMG') return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      const items = parseImagesFromHtml(html);
      if (!items.length) return;
      const clicked = normalizeMediaUrl(target.getAttribute('src') || target.src);
      let index = 0;
      for (let i = 0; i < items.length; i += 1) {
        if (normalizeMediaUrl(items[i].src) === clicked) { index = i; break; }
      }
      e.preventDefault();
      setState({ open: true, items, index });
    } catch (_) { /* no-op */ }
  }, []);

  const element = state.open && state.items.length > 0 ? (
    <ImageLightbox
      isOpen
      src={state.items[state.index]?.src}
      alt={state.items[state.index]?.alt}
      caption={`${state.index + 1} / ${state.items.length}`}
      items={showThumbnails ? state.items : []}
      activeIndex={state.index}
      variant={variant}
      tone={tone}
      onClose={close}
      onPrev={state.items.length > 1 ? () => setState((prev) => ({ ...prev, index: (prev.index - 1 + prev.items.length) % prev.items.length })) : undefined}
      onNext={state.items.length > 1 ? () => setState((prev) => ({ ...prev, index: (prev.index + 1) % prev.items.length })) : undefined}
      onSelect={showThumbnails ? (index) => setState((prev) => ({
        ...prev,
        index: Math.max(0, Math.min(index, prev.items.length - 1)),
      })) : undefined}
    />
  ) : null;

  const lightboxEl = element && portalRoot
    ? ReactDOM.createPortal(element, portalRoot)
    : element;

  return { lightboxEl, openWithItems, getRichContentClickHandler, close };
}
